import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';
import PageHeader from '../../components/PageHeader';

interface ConflictRow {
  conflict_log_id: string;
  business_id: string;
  asset_id: string | null;
  asset_name: string | null;
  attempted_by_user_id: string | null;
  offline_loan_id: string | null;
  offline_check_id: string | null;
  conflict_check_id: string | null;
  conflict_type: string | null;
  resolution: string;
  resolved_at: string | null;
  override_check_id: string | null;
  detected_at: string;
  created_at: string;
  attempted_by_first_name: string | null;
  attempted_by_last_name: string | null;
}

const SyncConflicts: React.FC = () => {
  const [rows, setRows] = useState<ConflictRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchConflicts = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Not authenticated');
        return;
      }
      const res = await axios.get(`${API_BASE_URL}/safety-checks/loan-conflict-log`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.success && Array.isArray(res.data.data)) {
        setRows(res.data.data);
      } else {
        setRows([]);
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load conflict log');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConflicts();
  }, []);

  const conflictTypeLabel = (type: string | null) => {
    if (!type) return 'Unknown';
    if (type === 'ASSET_LOCKED') return 'Asset in use (older check blocked)';
    if (type === 'LOAN_ALREADY_ACTIVE') return 'Asset already on loan';
    return type;
  };

  return (
    <Box sx={{ p: 2 }}>
      <PageHeader title="Sync conflicts" subtitle="Blocked loan/check syncs when multiple people used the same asset offline" />
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Detected</TableCell>
                <TableCell>Asset</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Attempted by</TableCell>
                <TableCell>Resolution</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No conflict entries</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((r) => (
                  <TableRow key={r.conflict_log_id}>
                    <TableCell>
                      {r.detected_at ? new Date(r.detected_at).toLocaleString() : '—'}
                    </TableCell>
                    <TableCell>{r.asset_name || r.asset_id || '—'}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={conflictTypeLabel(r.conflict_type)}
                        color={r.conflict_type === 'ASSET_LOCKED' ? 'warning' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {[r.attempted_by_first_name, r.attempted_by_last_name].filter(Boolean).join(' ') || r.attempted_by_user_id || '—'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={r.resolution || 'pending'}
                        color={
                          r.resolution === 'overridden' ? 'success' :
                          r.resolution === 'cancelled' ? 'default' : 'warning'
                        }
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default SyncConflicts;
